import React, { MouseEvent } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import PuzzleCell from './PuzzleCell';
import {
  PuzzleCellState,
  selectHighlight,
  selectLeftNumbersCorrect,
  selectPuzzle,
  selectPuzzleAnswer,
  selectSolved,
  selectTopNumbersCorrect,
  setPuzzleCell,
} from '../../../redux/puzzle-slice';
import { useAppDispatch } from '../../../redux/store';

export const borderClass = (
  x: number,
  w: number,
  baseClass: string,
  addedClass: string
): string => {
  let d = 5;
  if (x + 1 === w) {
    return baseClass;
  }
  if (w % 5 !== 0) {
    d = 4;
  }
  if ((x + 1) % d !== 0) {
    return baseClass;
  }
  return classnames(baseClass, addedClass);
};

const nextState: Record<PuzzleCellState, PuzzleCellState> = {
  [PuzzleCellState.Empty]: PuzzleCellState.On,
  [PuzzleCellState.On]: PuzzleCellState.Off,
  [PuzzleCellState.Off]: PuzzleCellState.Empty,
};

const Puzzle: React.FC = () => {
  const highlight = useSelector(selectHighlight);
  const puzzle = useSelector(selectPuzzle);
  const answer = useSelector(selectPuzzleAnswer);
  const solved = useSelector(selectSolved);
  const topNumbersCorrect = useSelector(selectTopNumbersCorrect);
  const leftNumbersCorrect = useSelector(selectLeftNumbersCorrect);
  const dispatch = useAppDispatch();
  const [curDrag, setCurDrag] = React.useState<PuzzleCellState>(
    PuzzleCellState.Empty
  );
  const [isDragging, setIsDragging] = React.useState(false);

  const setCell = React.useCallback(
    (x: number, y: number, value: PuzzleCellState) => {
      dispatch(setPuzzleCell({
        x,
        y,
        value,
      }));
    },
    [dispatch]
  );

  const onPointerDown = React.useCallback(
    (evt: React.PointerEvent, x: number, y: number) => {
      evt.preventDefault();
      const v = answer[x][y];
      let nextDrag = nextState[v];
      console.log(evt.pointerType, evt.button, v, nextDrag);
      if (evt.pointerType === 'mouse') {
        if (v === PuzzleCellState.Empty) {
          if (evt.button === 0) {
            nextDrag = PuzzleCellState.On;
          } else {
            nextDrag = PuzzleCellState.Off;
          }
        } else {
          nextDrag = PuzzleCellState.Empty;
        }
      }
      setCurDrag(nextDrag);
      setCell(x, y, nextDrag);
      setIsDragging(true);
    },
    [answer, setCell]
  );

  const onPointerMove = React.useCallback(
    (evt: React.PointerEvent) => {
      if (!isDragging) {
        return;
      }
      evt.preventDefault();
      const el = document.elementFromPoint(
        evt.clientX,
        evt.clientY,
      );
      if (!el) {
        return;
      }
      const row = el.getAttribute('data-row');
      const col = el.getAttribute('data-col');
      if ((row || row === '0') && (col || col === '0')) {
        const x = parseInt(col);
        const y = parseInt(row);
        setCell(x, y, curDrag);
      }
    },
    [curDrag, setCell, isDragging]
  );

  const onPointerUp = React.useCallback(
    (evt: React.PointerEvent, x: number, y: number) => {
      evt.preventDefault();
      setIsDragging(false);
      setCurDrag(PuzzleCellState.Empty);
    },
    []
  );

  const mouseOut = React.useCallback((evt: MouseEvent) => {
    evt.preventDefault();
    setIsDragging(false);
  }, []);

  if (!puzzle) {
    return null;
  }

  const {
    topNumbers,
    leftNumbers,
    size,
  } = puzzle;

  return (
    <table
      id='nonogram'
      cellSpacing='0'
      cellPadding='1'
      className={classnames(
        'my-0 mx-auto border-0',
        '[&_td]:border [&_*]:border-black dark:[&_*]:border-gray-300',
        solved ? 'solved' : ''
      )}
    >
      <tbody>
        <tr className=''>
          <td className='!border-b-2 !border-r-2' onMouseOver={mouseOut}>
            &nbsp;
          </td>
          {topNumbers.map((arr, idx) => (
            <td
              valign='bottom'
              className={classnames(
                borderClass(idx, size, '!border-b-2', '!border-r-2'),
                highlight && topNumbersCorrect[idx] && 'text-green-500',
              )}
              key={idx}
              onMouseOver={mouseOut}
            >
              {arr.map((n, aa) => (
                <React.Fragment key={aa}>
                  {n}
                  <br />
                </React.Fragment>
              ))}
            </td>
          ))}
        </tr>
        {leftNumbers.map((arr, row_i) => (
          <tr
            key={row_i}
            className={''}
          >
            <td
              className={classnames(
                'px-2 text-right !border-r-2',
                highlight && leftNumbersCorrect[row_i] && 'text-green-500',
                borderClass(row_i, size, '', '!border-b-2'),
              )}
              onMouseOver={mouseOut}
            >
              {arr.join(' ')}
            </td>
            {topNumbers.map((arr, col_i) => (
              <PuzzleCell
                y={row_i}
                x={col_i}
                className={classnames(
                  borderClass(
                    col_i,
                    size,
                    `puzzle-anim-${size - row_i + col_i}`,
                    '!border-r-2',
                  ),
                  borderClass(row_i, size, '', '!border-b-2'),
                )}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                key={`${row_i}x${col_i}`}
              />
            ))}
            <td className='border-none' onMouseOver={mouseOut} />
          </tr>
        ))}
        <tr>
          <td className='border-none' onMouseOver={mouseOut} />
          {topNumbers.map((arr, col_i) => (
            <td
              className='border-none'
              onMouseOver={mouseOut}
              key={col_i}
            />
          ))}
        </tr>
      </tbody>
    </table>
  );
};

export default Puzzle;
